import { useFormik } from 'formik';
import { mutate } from 'swr';
import * as Yup from 'yup';
import { consignmentService } from 'services';
import { useAuth } from 'hooks';
import { Button, Heading, Input, Select, Text } from 'components';
import { formValidations, messages, swrKeys } from 'helpers';
import { FontSize, FontWeight, actions, deliveryFailReasons } from 'commons';
import * as S from './styles';

interface Props {
  consignmentCode: string;
}

export function ReportFail({ consignmentCode }: Props) {
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;

  const nameDisplay = 'Nome do entregador';
  const reasonDisplay = 'Motivo';

  const validationSchena = Yup.object().shape({
    name: Yup.string().required(nameDisplay),
    reason: Yup.string().required(reasonDisplay),
  });

  function closeModal() {
    document.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
      })
    );
  }

  const formReport = useFormik({
    initialValues: {
      name: '',
      cpf: '',
      reason: '',
    },

    validateOnBlur: false,

    validateOnChange: false,

    validationSchema: validationSchena,

    validate: (values) => {
      formValidations.validationSchema({
        schema: validationSchena,
        values,
      });
    },

    onSubmit: async (values) => {
      const { name: store } = selectedStore;
      const { name, cpf, reason } = values;

      const response = await consignmentService.postCollect({
        consignmentCode,
        name: name,
        cpf: cpf || undefined,
        action: actions.shippingFail,
        store: store || '',
        deliveryFailReason: reason,
      });

      if (response.status === 200) {
        mutate(swrKeys.consignments({ key: store }));

        messages([
          { errorCode: 'FRONT-RE-DELIVERY_FAIL_CONFIRMED' },
          { subject: consignmentCode, subjectType: 'consignmentCode' },
        ]);

        closeModal();
      }
    },
  });

  const { values, errors, handleChange, handleSubmit, isSubmitting } =
    formReport;

  return (
    <S.Form onSubmit={handleSubmit}>
      <S.TextWrapper>
        <Heading size={FontSize.font16} weight={FontWeight.bold}>
          Falha na entrega
        </Heading>

        <Text size={FontSize.font12}>
          Ao reportar a falha na entrega, o time de backoffice entenderá que o
          <strong> pedido retornou para loja</strong> e necessita de uma nova
          coleta.
        </Text>

        <Text size={FontSize.font12}>
          Informe os<strong> dados do entregador</strong> e o motivo da falha na
          entrega:
        </Text>
      </S.TextWrapper>

      <Input
        name="name"
        label={nameDisplay}
        placeholder="Digite o nome completo"
        onChange={handleChange}
        value={values.name}
        status={errors.name ? 'error' : 'default'}
        disabled={isAdmin}
      />

      <Input
        mask="999.999.999-99"
        name="cpf"
        label="CPF do entregador"
        placeholder="000.000.000-00"
        type="tel"
        onChange={handleChange}
        value={values.cpf}
        status={errors.cpf ? 'error' : 'default'}
        disabled={isAdmin}
      />

      <S.InputWrapper>
        <S.Label htmlFor="reason">Motivo</S.Label>

        <Select.Wrapper
          name="reason"
          onChange={handleChange}
          options={deliveryFailReasons}
          status={errors.reason ? 'error' : 'default'}
          disabled={isAdmin}
        >
          <Select.Input placeholder="Selecione o Motivo..." />

          <Select.StatusArrow />
        </Select.Wrapper>
      </S.InputWrapper>

      <Button
        type="submit"
        variant="danger"
        full
        disabled={isSubmitting || isAdmin}
      >
        {isSubmitting ? 'Enviando' : 'Registrar Falha Na Entrega'}
      </Button>
    </S.Form>
  );
}
